/**
 * src/components/voice/NoteSessionCard.jsx — single voice session card.
 *
 * Plan: docs/plans/2026-05-01-voice-sessions-and-image-sync-plan.md (V-3).
 *
 * Compact row with status chip + label + dismiss. Click expands to show
 * transcript (when ready) or error message + retry CTA.
 *
 * Status chip mapping:
 *   queued     → muted blue, "Queued"
 *   uploading  → blue spinner, "Uploading"
 *   processing → blue spinner, "Transcribing"
 *   ready      → green check, "Ready"
 *   error      → red, "Failed" + retry button
 */
import { useState, useEffect } from 'react';
import {
  Mic, X, Loader2, CheckCircle2, AlertCircle, Clock, RotateCw, ChevronDown, ChevronUp,
  Download,
} from 'lucide-react';
import { getRecordingBlob, forceRetryRecording } from '../../lib/voiceQueue.js';

// CEO 2026-05-06 ("Pase" stuck 166 min) — mark a session as stuck when the
// upload pipeline isn't going to self-recover. Triggers the recovery
// affordances (Download + Force Retry) on the card.
const STUCK_AFTER_MS = 5 * 60 * 1000;
function isStuckSession(session, now) {
  if (!session || session.status === 'ready' || session.status === 'error') return false;
  if ((session.retry_count || 0) > 0) return true;
  const elapsed = session.created_at ? now - session.created_at : 0;
  return elapsed > STUCK_AFTER_MS;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so Safari has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Phase A (CEO 2026-05-03 evening UAT) — full state machine. Adds
// 'uploaded' (registered with server, awaiting transcription) and
// 'classifying' (post-transcript classifier running) so the user can
// follow what the server is actually doing instead of seeing the card
// flash 'ready' immediately after upload step.
const CHIP = {
  queued:      { bg: 'rgba(59,130,246,0.10)',  color: 'var(--info)', label: 'Queued',       Icon: Clock },
  uploading:   { bg: 'rgba(59,130,246,0.12)',  color: 'var(--info)', label: 'Uploading',    Icon: Loader2, spin: true },
  uploaded:    { bg: 'rgba(59,130,246,0.12)',  color: 'var(--info)', label: 'Awaiting',     Icon: Loader2, spin: true },
  processing:  { bg: 'rgba(59,130,246,0.12)',  color: 'var(--info)', label: 'Transcribing', Icon: Loader2, spin: true },
  classifying: { bg: 'rgba(59,130,246,0.12)',  color: 'var(--info)', label: 'Classifying',  Icon: Loader2, spin: true },
  ready:       { bg: 'rgba(34,197,94,0.12)',   color: 'var(--ok)',   label: 'Ready',        Icon: CheckCircle2 },
  error:       { bg: 'rgba(239,68,68,0.12)',   color: 'var(--bad)',  label: 'Failed',       Icon: AlertCircle },
};

const SCOPE_NAMES = {
  project: 'Project',
  task: 'Task',
  deal: 'Deal',
  bundle: 'Bundle',
  task_image: 'Image description',
};

// CEO ask 2026-05-03: prefer human label persisted at enqueue time
// (e.g. project name "MY Simulation") over the UUID slice fallback.
function scopeLabel(session) {
  if (session?.scope_label) return session.scope_label;
  const { scope_type, scope_id } = session || {};
  if (!scope_type) return 'Voice note';
  const tail = scope_id ? ` · ${String(scope_id).slice(-6)}` : '';
  return `${SCOPE_NAMES[scope_type] || scope_type}${tail}`;
}

function formatBytes(bytes) {
  if (typeof bytes !== 'number' || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatElapsed(ms) {
  if (typeof ms !== 'number' || ms < 0) return null;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function statusDetail(session) {
  // Granular sub-status (CEO 2026-05-03 mobile UAT). Now extended to cover
  // the server-side states the tray polls (uploaded / classifying) plus
  // chunked progress (chunks_done/chunks_total).
  const { status, retry_count, error_message, chunks_done, chunks_total, upload_pct } = session || {};
  if (status === 'queued') {
    return retry_count > 0
      ? `Waiting to retry · attempt ${retry_count + 1}`
      : 'Waiting for worker';
  }
  if (status === 'uploading') {
    return typeof upload_pct === 'number' ? `Uploading to storage · ${upload_pct}%` : 'Uploading to storage';
  }
  if (status === 'uploaded') return 'Server preparing transcription';
  if (status === 'processing') {
    if (chunks_total > 1 && typeof chunks_done === 'number') {
      return `Transcribing chunk ${chunks_done + 1} of ${chunks_total}`;
    }
    return 'Transcribing';
  }
  if (status === 'classifying') return 'Classifying transcript';
  if (status === 'error') return error_message || 'Failed';
  return null;
}

export default function NoteSessionCard({ session, onDismiss, onRetry }) {
  const [expanded, setExpanded] = useState(false);
  // Live elapsed counter so the user has movement on mobile when the
  // backend takes >1min on a long file. Re-renders once per second.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const inFlight = session?.status === 'queued'
                  || session?.status === 'uploading'
                  || session?.status === 'uploaded'
                  || session?.status === 'processing'
                  || session?.status === 'classifying';
    if (!inFlight) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [session?.status]);

  const chip = CHIP[session.status] || CHIP.queued;
  const ChipIcon = chip.Icon;
  const hasTranscript = !!session.transcript_raw;
  const canExpand = hasTranscript || session.status === 'error';
  const inFlight = session.status === 'queued'
                || session.status === 'uploading'
                || session.status === 'uploaded'
                || session.status === 'processing'
                || session.status === 'classifying';
  // Direct File→MinIO uploads (session.direct) have NO IndexedDB blob, so the
  // Download / Force-Retry recovery affordances (which read IDB) don't apply —
  // gate them off. TICKET-UPLOAD-PROGRESS-UX-T1.
  const stuck = !session.direct && isStuckSession(session, now);
  const [recoverBusy, setRecoverBusy] = useState(null); // 'download' | 'retry' | null
  const [recoverNote, setRecoverNote] = useState(null);

  async function handleDownload(e) {
    e.stopPropagation();
    setRecoverBusy('download');
    setRecoverNote(null);
    try {
      const mime = session.mime_type || 'audio/webm';
      const blob = await getRecordingBlob(session.recording_id, mime);
      if (!blob) {
        setRecoverNote('Audio is no longer in IndexedDB on this device.');
        return;
      }
      const ext = mime.includes('webm') ? 'webm' : mime.includes('m4a') ? 'm4a' : 'audio';
      const safeLabel = (scopeLabel(session) || 'voice-note').replace(/[^a-z0-9-]+/gi, '-');
      const ts = new Date(session.created_at || Date.now()).toISOString().replace(/[:.]/g, '-');
      downloadBlob(blob, `${safeLabel}-${ts}.${ext}`);
      setRecoverNote(`Saved ${(blob.size / 1024 / 1024).toFixed(1)} MB.`);
    } catch (err) {
      setRecoverNote(`Download failed: ${err?.message || 'unknown error'}`);
    } finally {
      setRecoverBusy(null);
    }
  }

  async function handleForceRetry(e) {
    e.stopPropagation();
    setRecoverBusy('retry');
    setRecoverNote(null);
    try {
      await forceRetryRecording(session.recording_id);
      setRecoverNote('Re-queued. Worker will pick it up on the next tick.');
    } catch (err) {
      setRecoverNote(`Force retry failed: ${err?.message || 'unknown error'}`);
    } finally {
      setRecoverBusy(null);
    }
  }
  const elapsedMs = session.created_at ? now - session.created_at : null;
  const sizeStr = formatBytes(session.file_size_bytes);
  const elapsedStr = formatElapsed(elapsedMs);
  const detail = statusDetail(session);
  const subline = [sizeStr, elapsedStr ? `${elapsedStr} elapsed` : null, detail]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      data-testid={`note-session-card-${session.recording_id}`}
      style={{
        background: 'var(--card)',
        borderRadius: 10,
        border: '1px solid var(--bdr)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 10px', minHeight: 44,
          cursor: canExpand ? 'pointer' : 'default',
        }}
        onClick={() => canExpand && setExpanded((v) => !v)}
        role={canExpand ? 'button' : undefined}
        tabIndex={canExpand ? 0 : -1}
        onKeyDown={(e) => {
          if (!canExpand) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
      >
        <Mic size={14} color="var(--accent)" style={{ flexShrink: 0 }} />
        <div style={{
          flex: 1, minWidth: 0,
          display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          <span
            data-testid="note-session-label"
            style={{
              fontSize: 12, fontWeight: 600, color: 'var(--t1)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {scopeLabel(session)}
          </span>
          {subline && (
            <span
              data-testid="note-session-subline"
              style={{
                fontSize: 10, color: 'var(--tm)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {subline}
            </span>
          )}
        </div>
        <span
          data-testid="note-session-chip"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600,
            background: chip.bg, color: chip.color, flexShrink: 0,
          }}
        >
          <ChipIcon size={10} className={chip.spin ? 'animate-spin' : ''} />
          {chip.label}
        </span>
        {canExpand && (
          expanded
            ? <ChevronUp size={12} color="var(--tm)" style={{ flexShrink: 0 }} />
            : <ChevronDown size={12} color="var(--tm)" style={{ flexShrink: 0 }} />
        )}
        {stuck && (
          <>
            <button
              data-testid={`note-session-download-${session.recording_id}`}
              onClick={handleDownload}
              disabled={recoverBusy !== null}
              aria-label={'Download audio file'}
              title={'Save audio to your device — keeps the original recording even if upload never succeeds'}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minHeight: 44, minWidth: 44,
                border: 'none', background: 'transparent', color: 'var(--info)',
                cursor: recoverBusy ? 'default' : 'pointer', borderRadius: 6, padding: '8px 12px',
                opacity: recoverBusy === 'download' ? 0.5 : 1, flexShrink: 0,
              }}
            >
              <Download size={14} />
            </button>
            <button
              data-testid={`note-session-force-retry-${session.recording_id}`}
              onClick={handleForceRetry}
              disabled={recoverBusy !== null}
              aria-label={'Force retry upload'}
              title={'Reset retry counters and re-queue for upload'}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minHeight: 44, minWidth: 44,
                border: 'none', background: 'transparent', color: 'var(--info)',
                cursor: recoverBusy ? 'default' : 'pointer', borderRadius: 6, padding: '8px 12px',
                opacity: recoverBusy === 'retry' ? 0.5 : 1, flexShrink: 0,
              }}
            >
              <RotateCw size={14} className={recoverBusy === 'retry' ? 'animate-spin' : ''} />
            </button>
          </>
        )}
        <button
          data-testid={`note-session-dismiss-${session.recording_id}`}
          onClick={(e) => { e.stopPropagation(); onDismiss?.(session.recording_id); }}
          aria-label={inFlight ? 'Hide — upload continues in background' : 'Dismiss'}
          title={inFlight ? 'Hide — upload continues in background' : 'Dismiss'}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, minHeight: 44, minWidth: 44,
            border: 'none', background: 'transparent', color: 'var(--tm)',
            cursor: 'pointer', borderRadius: 6, padding: '8px 12px',
            flexShrink: 0,
          }}
        >
          <X size={14} />
        </button>
      </div>

      {recoverNote && (
        <div
          data-testid={`note-session-recover-note-${session.recording_id}`}
          style={{
            padding: '6px 12px',
            fontSize: 11,
            color: 'var(--tm)',
            background: 'var(--info-tint)',
            borderTop: '1px solid var(--bdr)',
          }}
        >
          {recoverNote}
        </div>
      )}

      {session.status === 'uploading' && typeof session.upload_pct === 'number' && (
        <progress
          data-testid="note-session-upload-progress"
          value={session.upload_pct}
          max={100}
          aria-label={`Uploading ${session.upload_pct}%`}
          style={{
            display: 'block',
            width: '100%',
            height: 4,
            border: 'none',
            borderRadius: 0,
            appearance: 'none',
            WebkitAppearance: 'none',
            backgroundColor: 'var(--info-tint)',
            color: 'var(--info)',
            overflow: 'hidden',
          }}
        />
      )}

      {inFlight && session.chunks_total > 1 && (
        <progress
          data-testid="note-session-progress"
          value={session.chunks_done ?? 0}
          max={session.chunks_total}
          aria-label={`Transcribing chunk ${(session.chunks_done ?? 0) + 1} of ${session.chunks_total}`}
          style={{
            display: 'block',
            width: '100%',
            height: 4,
            border: 'none',
            borderRadius: 0,
            appearance: 'none',
            WebkitAppearance: 'none',
            backgroundColor: 'var(--info-tint)',
            color: 'var(--info)',
            overflow: 'hidden',
          }}
        />
      )}

      {expanded && (
        <div style={{
          padding: '8px 12px 12px',
          borderTop: '1px solid var(--bdr)',
          fontSize: 12, color: 'var(--t1)',
          background: 'var(--glass)',
        }}>
          {session.status === 'error' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--bad)' }}>
              <span style={{ flex: 1 }}>{session.error_message || 'Processing failed'}</span>
              {onRetry && (
                <button
                  data-testid={`note-session-retry-${session.recording_id}`}
                  onClick={(e) => { e.stopPropagation(); onRetry(session.recording_id); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: 'var(--navy-surface)', color: 'var(--on-navy)',
                    border: 'none', cursor: 'pointer', minHeight: 44,
                  }}
                >
                  <RotateCw size={11} /> Retry
                </button>
              )}
            </div>
          )}
          {hasTranscript && (
            <div style={{
              whiteSpace: 'pre-wrap', lineHeight: 1.5,
              maxHeight: 200, overflowY: 'auto',
            }}>
              {session.transcript_raw}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
